import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { RootState } from '@src/store'
import type { IActionsCallbacks, ILang, ITemplate, IWorkspace } from '@src/interfaces'
import {
  updateTemplate,
  setCurrentTemplate as setCurrentTemplateStore,
  show
} from '@src/slices/configuration.slice'
import { Checkbox, Col, Divider, Form, Input, Radio, Row, Select, Switch, Tabs } from 'antd'
import Wysiwyg from '@src/modules/atoms/wysiwyg'
import TitleInput from '@src/modules/atoms/title-input'
import EditableButtonsFooter from '@src/modules/molecules/editable-buttons-footer'
import {
  getDocumentTypes,
  getTemplateByToken,
  getVariables,
  getWorkspaces
} from '@src/services/config.service'
import LanguageSelector, { Languages } from '@src/modules/atoms/language-selector'
import { currentUserLang } from '@src/helpers/current-user-lang'
import ConfirmationActionModal from '@src/modules/molecules/confirmation-action-modal'
import SaveOrDiscardChangeModal from '@src/modules/molecules/save-discard-change-modal'
import { UseExternalComponentContext } from '@src/context/ExternalComponentsContext'
import { variablesParser } from '../../../helpers/variables-parser'
import styles from './styles.module.scss'
import FormLabel from '@src/modules/atoms/form-label'
import { SignOrders } from '@src/enums/sign-order'
import { FormComponentProps } from 'antd/lib/form'
import { ISigner } from '@src/models/signers'
import { CounterpartsTable } from '@src/modules/molecules/tables/counterparts-table'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { faArrowLeftToArc } from '@fortawesome/pro-solid-svg-icons/faArrowLeftToArc'
import { faArrowRightToArc } from '@fortawesome/pro-solid-svg-icons/faArrowRightToArc'
import { AddCounterpartsModal } from '../modal/add-counterparts'
import { saveVars } from '@src/slices/configuration.slice'

// This variable is unfortunately necessary because in the first render,
// the WYWIWYG component update the content of the template, so, the safety copy
// have already change against the currentTemplate, even if we don't type anything
// So this variable will change only at the first onChange of WYWIWYG component (changing to false)
// and when we change the language tab (changing to true). The last one is because each template
// is handled like a new one, regardless of the language.
let firstSaved = true

interface IEditable extends FormComponentProps {
  template: ITemplate
  isNew: boolean
  isSignatureFlow?: boolean
  biometric?: boolean
  documentType?: boolean
  tags?: boolean
  templateHeaderNode: React.ReactNode
  actionsCallback?: IActionsCallbacks
  t?: any
  handleReturn?: () => void
  form: any
  variableVisibility?: any
  handleSearch?: () => void
  filterWorkspaceCallback?: (key: string) => boolean
}

interface IDocumentTypes {
  id: number
  document_files_total: number
  name: {
    [key: string]: string
  }
  templates_total: number
  token: string
}

const { Option } = Select
const { TabPane } = Tabs
const userLang = currentUserLang()

const Editable: React.FC<IEditable> = ({
  template,
  isNew,
  templateHeaderNode,
  actionsCallback,
  isSignatureFlow = false,
  biometric = false,
  documentType = false,
  tags = false,
  t,
  form,
  variableVisibility,
  handleReturn,
  handleSearch,
  filterWorkspaceCallback
}) => {
  const dispatch = useDispatch()
  const baseProps = useSelector(({ baseProps }: RootState) => baseProps)
  const [lang, setLang] = useState<ILang>(() => {
    const selectedLang = Languages.find(({ code }) => code === userLang)
    return selectedLang || { code: 'es', label: 'Español' }
  })
  const showVariables = useSelector(show)
  const [currentTemplate, setCurrentTemplate] = useState<ITemplate>(template)
  const [safetyTemplateCopy, setSafetyTemplateCopy] = useState<ITemplate>(template)
  const [workspaces, setWorkspaces] = useState<IWorkspace[]>([])
  const [documentTypes, setDocumentTypes] = useState<IDocumentTypes[]>([])
  const [alert, setAlert] = useState(<div />)
  const { getFieldDecorator, getFieldValue, setFieldsValue } = form
  const [selectedSignOrder, setSelectedSignOrder] = useState<string>(SignOrders.Nobody)
  const [counterparts, setCounterparts] = useState<any[]>([])
  const [showCounterPartsModal, setShowCounterPartsModal] = useState<boolean>(false)
  const [disabledSave, setDisabledSave] = useState<boolean>(false)
  const signOrders = getFieldValue('sign_orders')
  const checkSignOrder = getFieldValue('check_sign_order')
  const orderedCounterparts = getFieldValue('ordered_signers')
  const minSigners = getFieldValue('min_signers')
  const biometricAuthentication = getFieldValue('biometric_authentication')

  //console.log('currentTemplate', currentTemplate)
  console.log('template', template)

  const tabs = [
    {
      title: 'Contenido',
      render: (
        <TitleInput required title='Contenido'>
          <Wysiwyg
            value={currentTemplate.content}
            onChange={(e: any) => {
              if (firstSaved) {
                setTimeout(() => {
                  setSafetyTemplateCopy({ ...safetyTemplateCopy, content: e })
                }, 100)
                firstSaved = false
              }
              onChange('content', e)
            }}
          />
        </TitleInput>
      )
    }
  ]

  useEffect(() => {
    if (selectedSignOrder === SignOrders.OnlyOwner) {
      setDisabledSave(false)
    } else if(selectedSignOrder === SignOrders.Nobody) {
      setDisabledSave(false)
      setFieldsValue({
        biometric_authentication: false
      })
    } else {
      counterparts?.length ? setDisabledSave(false) : setDisabledSave(true)
    }
  }, [selectedSignOrder, signOrders, counterparts])

  useEffect(() => {
    if (minSigners) {
      setFieldsValue({
        ordered_signers: false,
        min_signers: true
      })
    }
  }, [minSigners])

  useEffect(() => {
    if (orderedCounterparts) {
      setFieldsValue({
        min_signers: false,
        ordered_signers: true
      })
    }
  }, [orderedCounterparts])

  useEffect(() => {
    if (template?.sign_order) {
      const signatureFlow = JSON.parse(template.sign_order)
      if (signatureFlow) {
        setFieldsValue({
          sign_orders: signatureFlow.selectedSignOrder,
          check_sign_order: signatureFlow.checkSignOrder,
          ordered_signers: signatureFlow.counterparts?.order,
        })
        setSelectedSignOrder(signatureFlow.selectedSignOrder)
        setCounterparts(signatureFlow.counterparts?.counterparts)

        setTimeout(() => {
          setFieldsValue({
            ordered_signers: signatureFlow.counterparts?.order,
            min_signers: signatureFlow.counterparts?.minSigners,
            biometric_authentication: signatureFlow?.biometricAuthentication
          })
        }, 500)
      }
    }
  }, [])

  useEffect(() => {
    if(documentType) {
      getDocumentTypes(`${baseProps.baseUrl}/api/v1`, { page: 1, terms: '', items: 100 }).then(
        (data) => {
          setDocumentTypes(data)
        }
      )
    }
  }, [])

  const handleChangeDocumentType = (value: string) => {
    const addDocumentType = {
      ...currentTemplate,
      document_type: value
    }
    setCurrentTemplate(addDocumentType)
  }

  const updateCurrentTemplate = (template: ITemplate, lang: string) => {
    console.log('updateCurrentTemplate', template)
    setCurrentTemplate(template)
    setSafetyTemplateCopy(template)
    dispatch(
      setCurrentTemplateStore({
        template: {
          ...template,
          lang
        }
      })
    )
  }

  const onChange = async (prop: string, value: string | IWorkspace | string[]) => {
    console.log('---onChange----', currentTemplate)
    setCurrentTemplate({ ...currentTemplate, [prop]: value })
    dispatch(updateTemplate({ prop, value, isNew }))
  }

  const onChangeWorkspace = (selectedWorkspaceToken: any) => {
    const closeModal = () => {
      setAlert(<div />)
    }
    const selectedWorkspace = workspaces.find(({ token }) => token === selectedWorkspaceToken)!

    // If there is no content, do not alert about lost it.
    // just change the workspaces
    if (!template.content || template.content === '') {
      changeWorkspace(selectedWorkspace)
      return
    }

    setAlert(
      <ConfirmationActionModal
        title='Confirmar cambio de espacio de trabajo'
        content={
          <p>
            Al cambiar de espacio de trabajo perderás la información ingresada hasta el momento,
            ¿estás seguro que deseas continuar?
          </p>
        }
        onOk={() => {
          changeWorkspace(selectedWorkspace)
          closeModal()
        }}
        onCancel={closeModal}
      />
    )
  }

  const changeWorkspace = (selectedWorkspace: IWorkspace) => {
    const newTemplate = {
      ...currentTemplate,
      workspace: selectedWorkspace,
      content: ''
    }

    setCurrentTemplate(newTemplate)
    dispatch(updateTemplate({ prop: 'workspace', value: selectedWorkspace, isNew }))
    dispatch(updateTemplate({ prop: 'content', value: '', isNew }))
  }

  const onChangeLang = (selectedLang: ILang) => {
    const { title: currentTitle, content: currentContent } = currentTemplate
    const { title: safetyCopyTitle, content: safetyCopyContent } = safetyTemplateCopy

    if (currentTitle !== safetyCopyTitle || currentContent !== safetyCopyContent) {
      setAlert(
        <SaveOrDiscardChangeModal
          changeLang={() => {
            setLang(selectedLang)
          }}
          onClose={() => {
            setAlert(<div />)
          }}
        />
      )
      return
    }

    setLang(selectedLang)
  }

  const fetchWorkspaces = () => {
    getWorkspaces(baseProps).then(({ data }) => {
      const workspaces: IWorkspace[] = Object.keys(data)
        .filter((token: string) =>
          filterWorkspaceCallback ? filterWorkspaceCallback(token) : true
        )
        .map((token: string) => {
          return {
            title: data[token],
            token
          }
        })
      setWorkspaces(workspaces)
      onChange('workspace', workspaces[0])
    })
  }

  const fetchTemplateByToken = (lang: string) => {
    const { token: templateToken } = currentTemplate
    if (!templateToken) return

    getTemplateByToken(baseProps, templateToken, lang).then(({ data }) => {
      const { workspace: workspaceToken, workspace_title: workspaceTitle } = data.template
      const fetchedTemplate: ITemplate = {
        ...data?.template,
        document_type: data?.template?.document_type?.token,
        workspace: { token: workspaceToken, title: workspaceTitle }
      }
      updateCurrentTemplate(fetchedTemplate, lang)
    })
  }

  const fetchVariables = (workspaceToken: string) => {
    getVariables(baseProps, workspaceToken).then(({ data }) => {
      dispatch(saveVars({ variables: variablesParser(data, userLang) }))
    })
  }

  useEffect(() => {
    if (isNew) {
      fetchWorkspaces()
    }
  }, [])

  useEffect(() => {
    firstSaved = true
    const { code: langCode } = lang
    fetchTemplateByToken(langCode)
  }, [lang])

  useEffect(() => {
    if (currentTemplate.workspace) {
      fetchVariables(currentTemplate.workspace.token)
    }
  }, [currentTemplate.workspace])

  const handleOnChange = async (
    checkSignOrder: boolean | null,
    counterparts: object = [],
    selectedSignOrder: string | null,
    biometricAuthentication: boolean | null
  ) => {
    const newTemplate = {
      ...currentTemplate,
      sign_order: checkSignOrder
        ? {
            checkSignOrder,
            counterparts,
            selectedSignOrder,
            biometricAuthentication
          }
        : null
    }
    console.log('---handleOnChange----', newTemplate)
    await dispatch(setCurrentTemplateStore({
      template: {
        ...newTemplate,
        sign_order: JSON.stringify(newTemplate?.sign_order)
      }
    }))
    setCurrentTemplate(newTemplate)
  }

  const validateCounterparts = () => {
    if (selectedSignOrder === SignOrders.Nobody || selectedSignOrder === SignOrders.OnlyOwner) {
      return []
    } else {
      return counterparts
    }
  }

  useEffect(() => {
    if (checkSignOrder) {
      handleOnChange?.(
        checkSignOrder,
        {
          order: Boolean(orderedCounterparts),
          counterparts: validateCounterparts(),
          minSigners: Boolean(minSigners),
        },
        selectedSignOrder,
        biometricAuthentication
      )
    }
    else handleOnChange?.(false, { order: false, counterparts: [], minSigners: false }, null, false)
  }, [counterparts, selectedSignOrder, checkSignOrder, orderedCounterparts, minSigners, biometricAuthentication])

  const handleSignOrders = ({ target }: any) => {
    if (target === SignOrders.Nobody || target === SignOrders.OnlyOwner) {
      setCounterparts([])
    }
    setSelectedSignOrder(target.value)
  }

  const handleDeleteUsers = (users: ISigner[], token: string) => {
    return users.filter((item) => item.token !== token)
  }

  const handleRemove = (token: string) => {
    const newUsers = handleDeleteUsers(counterparts, token)
    setCounterparts(newUsers)
  }

  return (
    currentTemplate &&
    currentTemplate.workspace && (
      <div className='editable'>
        {alert}
        <Row gutter={16}>
          {!isNew && (
            <Col className='gutter-row' span={24}>
              <LanguageSelector
                defaultLang={lang}
                onSelectedLang={(selectedLang) => {
                  onChangeLang(selectedLang)
                }}
              />
            </Col>
          )}
          <Col className='gutter-row' span={12}>
            <TitleInput title='Espacio de trabajo'>
              <Select
                className={styles.selectWorkspaces}
                onChange={onChangeWorkspace}
                value={isNew ? currentTemplate.workspace.token : currentTemplate.workspace.title}
                disabled={!isNew}
              >
                {workspaces.map(({ title, token }, index) => (
                  <Option value={token} key={index}>
                    {title}
                  </Option>
                ))}
              </Select>
            </TitleInput>
          </Col>
          <Col className='gutter-row' span={12}>
            <TitleInput title='Nombre' required className={styles.title}>
              <Input
                onChange={(e) => onChange('title', e.target.value)}
                className={styles.input}
                value={currentTemplate.title}
              />
            </TitleInput>
          </Col>
        </Row>
        <Row gutter={16}>
          {tags && (
            <Col className='gutter-row' span={12}>
              <TitleInput title='Etiquetas'>
                <Select
                  mode='tags'
                  value={currentTemplate.tags}
                  className={styles.selectWorkspaces}
                  onChange={(values: string[]) => onChange('tags', values)}
                />
              </TitleInput>
            </Col>
          )}
        </Row>
        <div className={`rankmiTemplates-templateHeaderNode ${styles.templateHeaderNode}`}>
          {templateHeaderNode}
        </div>
        {documentType && (
          <Col span={24} className={`gutter-row ${styles.noPadding}`}>
            <TitleInput title='Categoria de documento'>
              <Select
                onChange={(value: string) => handleChangeDocumentType(value)}
                className={styles.selectWorkspaces}
                value={currentTemplate?.document_type}
              >
                {documentTypes?.map(({ name, token }, index) => (
                  <Option value={token} key={index}>
                    {name?.es}
                  </Option>
                ))}
              </Select>
            </TitleInput>
          </Col>
        )}
        <Col span={24} className={styles.containerShowCol}>
          <div className={styles.colWidth}>
            <div className={styles.subColWidth}>
              {showVariables ? (
                <React.Fragment>
                  <p onClick={() => variableVisibility?.()}>Ocultar variables</p>
                  <FontAwesomeIcon icon={faArrowLeftToArc as IconProp} />
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <p onClick={() => variableVisibility?.()}>Mostrar variables</p>
                  <FontAwesomeIcon icon={faArrowRightToArc as IconProp} />
                </React.Fragment>
              )}
            </div>
          </div>
        </Col>
        <Tabs defaultActiveKey='0' animated={false}>
          {tabs.map(({ title, render }, index) => (
            <TabPane tab={title} key={`${index}`}>
              {render}
            </TabPane>
          ))}
        </Tabs>
        {isSignatureFlow && (
          <div className={styles.rankmiSignatureNewDocumentForm}>
            <Row>
              <Col className={styles.containerSwitch}>
                <div className={styles.containerTitle}>
                  <div>
                    <FormLabel label={t('signature.newDocument.stepsForm.switchs.signatureFlow')} />
                    <p className={styles.subTitleSwitch}>
                      {t('signature.newDocument.stepsForm.switchs.templates.subtitleSignatureFlow')}
                    </p>
                  </div>
                  <div>
                    {getFieldDecorator('check_sign_order', {
                      valuePropName: 'checked',
                      initialValue: false
                    })(
                      <Switch
                        checkedChildren={t('signature.newDocument.stepsForm.switchs.content.yes')}
                        unCheckedChildren={t('signature.newDocument.stepsForm.switchs.content.not')}
                        className={styles.switch}
                      />
                    )}
                  </div>
                </div>
                {checkSignOrder && <Divider />}
              </Col>
              {checkSignOrder && (
                <div>
                  <Col>
                    <div className={selectedSignOrder !== SignOrders.Nobody ? styles.signers : styles.noSigners}>
                      <div className={styles.signersLabel}>
                        <span>
                          {t('signature.newDocument.stepsForm.signersRadioButton.sublabel')}
                        </span>
                      </div>
                      <Row>
                        {getFieldDecorator('sign_orders', {
                          rules: [{ required: true }],
                          preserve: false,
                          initialValue: selectedSignOrder
                        })(
                          <Radio.Group name='radiogroup' onChange={handleSignOrders}>
                            <Radio value={SignOrders.Nobody}>
                              {t(
                                'signature.newDocument.stepsForm.signersRadioButton.values.nobody'
                              )}
                            </Radio>
                            <Radio value={SignOrders.OnlyOwner}>
                              {t(
                                'signature.newDocument.stepsForm.signersRadioButton.values.onlyOwner'
                              )}
                            </Radio>
                            <Radio value={SignOrders.Everyone}>
                              {t(
                                'signature.newDocument.stepsForm.signersRadioButton.values.everyone'
                              )}
                            </Radio>
                            <Radio value={SignOrders.SignersFirst}>
                              {t(
                                'signature.newDocument.stepsForm.signersRadioButton.values.signersFirst'
                              )}
                            </Radio>
                            <Radio value={SignOrders.OwnerFirst}>
                              {t(
                                'signature.newDocument.stepsForm.signersRadioButton.values.ownerFirst'
                              )}
                            </Radio>
                            <Radio value={SignOrders.OnlySigners}>
                              {t(
                                'signature.newDocument.stepsForm.signersRadioButton.values.onlySigners'
                              )}
                            </Radio>
                          </Radio.Group>
                        )}
                      </Row>
                    </div>
                  </Col>
                  <Col>
                    {selectedSignOrder !== SignOrders.OnlyOwner &&
                      selectedSignOrder !== SignOrders.Nobody && (
                        <div className={styles.couterparts}>
                          <FormLabel
                            label={t('signature.newDocument.stepsForm.couterpartsInput.label')}
                          />
                          <div className={styles.counterpartsCheckbox}>
                            {signOrders !== SignOrders.Everyone &&
                              signOrders !== SignOrders.OnlyOwner &&
                              getFieldDecorator('ordered_signers', {
                                valuePropName: 'checked',
                                initialValue: false
                              })(
                                <Checkbox>
                                  {t('signature.newDocument.stepsForm.couterpartsInput.text')}
                                </Checkbox>
                              )}
                            {signOrders !== SignOrders.Nobody &&
                              signOrders !== SignOrders.OnlyOwner &&
                              signOrders !== SignOrders.OnlySigners &&
                              signOrders !== SignOrders.Multiple &&
                              getFieldDecorator('min_signers', {
                                valuePropName: 'checked',
                                initialValue: false
                              })(
                                <Checkbox className={styles.checkboxOnlyOne}>
                                  {t('signature.newDocument.stepsForm.onlyOneSignatureInput.text')}
                                </Checkbox>
                              )}
                          </div>
                          <CounterpartsTable
                            form={form}
                            t={t}
                            data={counterparts}
                            setCounterparts={setCounterparts}
                            onAddUser={() => setShowCounterPartsModal(true)}
                            onRemoveUser={handleRemove}
                          />
                        </div>
                      )}
                  </Col>
                </div>
              )}
            </Row>
            {showCounterPartsModal && (
              <AddCounterpartsModal
                open={showCounterPartsModal}
                selectedSigners={counterparts}
                t={t}
                onClose={() => setShowCounterPartsModal(false)}
                onConfirm={(users) => {
                  setCounterparts(users)
                  setShowCounterPartsModal(false)
                }}
              />
            )}
            {biometric && selectedSignOrder !== SignOrders.Nobody && (
              <div className={styles.containerBiometric}>
                {getFieldDecorator('biometric_authentication', {
                  valuePropName: 'checked',
                  initialValue: false
                })(
                  <Checkbox
                    className={styles.input}
                  >
                    <p className={styles.textOne}>Habilitar validación biométrica</p>
                    <p className={styles.textTwo}>
                      Requiere reconocimiento facial y validación de identidad antes de firmar. Para
                      más detalles
                      <a href='/' target='_blank'>
                        accede al Centro de ayuda.
                      </a>
                    </p>
                  </Checkbox>
                )}
              </div>
            )}
          </div>
        )}

        <UseExternalComponentContext
          component='registerSignatureOrderSigners'
          props={{
            onChange: handleOnChange,
            updateDataTemplate: template,
            t
          }}
        />
        <EditableButtonsFooter
          template={currentTemplate}
          isNew={isNew}
          disabled={disabledSave}
          handleReturn={handleReturn}
          updateTemplateCopy={() => {
            setSafetyTemplateCopy(currentTemplate)
          }}
          lang={lang}
          actionsCallback={actionsCallback}
          handleSearch={handleSearch}
        />
      </div>
    )
  )
}

export const EditableForm = Form.create<IEditable>({
  name: 'editable_form'
})(Editable)
